import { ProductResponseSettingsType, ProductSettingsType } from 'b2b-common/core/product/api/Product.types';

enum CART_ITEM_ERROR_TYPE {
  itemSoldOut = 'item_sold_out',
  maxOrderQuantityExceeded = 'max_order_quantity_exceeded',
  legacy = 'legacy',
  missingCertificate = 'missing_certificate',
  itemOutsideOfCoreAssortment = 'item_outside_of_core_assortment',
  itemMaxOrderQuantityExceeded = 'item_max_order_quantity_exceeded',
  priceChanged = 'price_changed',
  itemRemovedFromCart = 'item_removed_from_cart',
}

export interface EclassVariant {
  version: '10' | '11' | '12' | '13' | '5' | '6' | '7' | '8' | '9';
  value: string;
}

export interface UnspscCodeVariant {
  version: '19' | '22' | '24';
  value: string;
}

interface FetchCartItemResponseType {
  analytics: null | {
    categories: {
      depth: number;
      label: string;
      url: string;
    }[];
    title: string;
  };
  cart: {
    price: number;
    priceWithoutVat: number;
    quantity: number;
    reference: string;
  };
  category: {
    label: string;
    url: string;
  };
  coverLink: string;
  errors: string[];
  expectedRedeliveryDate: string | null;
  manufacturer: {
    id: string;
    name: string;
  };
  name: string;
  priceWithVat: number;
  quantity: number;
  reference: string;
  settings: ProductResponseSettingsType,
  single: {
    currency: string;
    discount: number;
    discountWithoutVat: number;
    final: number;
    finalWithoutVat: number;
    price: number;
    priceWithoutVat: number;
  };
  sku: string;
  eClassNr: string;
  unspscCode: string;
  itemNumbers: {
    ean: string;
    nav: string;
    eClassNr: string;
    unspscCode: string;
    statisticGroup: string;
    eClassNumbers: EclassVariant[];
    unspscCodes: UnspscCodeVariant[];
  };
  taxRate: string;
  total: {
    currency: string;
    discount: number;
    discountWithoutVat: number;
    final: number;
    finalWithoutVat: number;
    price: number;
    priceWithoutVat: number;
  };
}

interface FetchCartRemovedItemResponseType {
  errors: string[];
  sku: string;
}

interface FetchCartSummaryResponseType {
  discount: number;
  discountVat: number;
  discountWithoutVat: number;
  final: number;
  finalVat: number;
  finalWithoutVat: number;
  price: number;
  priceVat: number;
  priceWithoutVat: number;
  validated: number;
  voucher: {
    code: string | null;
    description: string | null;
    valid: number | null;
  };
  lockerId?: string | null;
  costCenterUuid?: string | null;
}

type FetchCartResponseType = {
  items: FetchCartItemResponseType[];
  removedItems: FetchCartRemovedItemResponseType[];
  summary: FetchCartSummaryResponseType;
};

type CartProductDetailsType = {
  reference: string | null;
  quantity: number;
  price: number;
  priceWithoutVat: number;
}

type CartProductAnalyticsType = {
  title: string;
  categories: string[];
}

type CartProductSingleType = {
  currency: string;
  discount: number;
  discountWithoutVat: number;
  final: number;
  finalWithoutVat: number;
  price: number;
  priceWithoutVat: number;
}

type CartProductTotalType = {
  currency: string;
  price: number;
  priceWithoutVat: number;
  discountWithoutVat: number;
  final: number;
  finalWithoutVat: number;
}

type CartProductType = {
  analytics: CartProductAnalyticsType;
  cart: CartProductDetailsType;
  category: {
    name: string;
    url: string;
  };
  certificates?: string[];
  coverUri: string;
  cover: string;
  errors: string[];
  isActive: boolean;
  manufacturer: {
    id: string;
    name: string;
  };
  name: string;
  settings: ProductSettingsType;
  single: CartProductSingleType;
  sku: string;
  total: CartProductTotalType;
};

type CartSummaryType = {
  discount: number;
  dropShipmentCosts?: number | null;
  final: number;
  finalVat: number;
  finalWithoutVat: number;
  isValidated: boolean;
  partialShippingCosts?: number | null;
  paymentCosts?: number | null;
  price: number;
  priceVat: number;
  priceWithoutVat: number;
  shippingCosts?: number;
  smallQuantitiesCost?: number | null;
  voucher: {
    code: string;
    description: string;
    isValid: boolean;
  } | null;
  lockerId?: string | null;
  costCenterUuid?: string | null;
};

type CartType = {
  products: CartProductType[];
  removedItemsSKUs: string[];
  summary: CartSummaryType;
};

type AddItemToCartResponseType = {
  cart: {
    size: number;
    value: number;
  };
  item: {
    analytics: {
      categories: {
        depth: number;
        label: string;
        url: string;
      }[];
      title: string;
    };
    currentQuantity: number;
    errors: string[];
    insertedQuantity: number;
    maxAllowedQuantity: number;
    pricing: {
      list: {
        price: number;
        priceWithoutVat: number;
      };
      taxRate: string;
      user: {
        price: number;
        priceWithoutVat: number;
      };
    };
    productName: string;
  };
};

type CartStatusWithAddedItemType = {
  cart: {
    count: number;
    value: number;
    lastInsertedQuantity: number | null;
    messages: string[],
    isLastInsertedNewItem?: boolean;
  },
  item: {
    price: number,
    priceWithoutVat: number,
    categories: string[],
    addedQuantity: number,
    currentQuantity: number,
    errors: string[],
    maxAllowedQuantity: number,
    productName: string,
  }
};

type CartItemType = {
  quantity: number,
  reference: string
  sku: string,
  title: string
}

type CartItemFailedType = CartItemType & {
  error: string;
};

type AddItemsToCartResponseType = {
  added: CartItemType[],
  failed: CartItemFailedType[],
}

type CartItemsErrorGroupType = {
  description?: CART_ITEM_ERROR_TYPE,
  items: CartItemFailedType[],
};

type AddItemsToCartType = {
  added: CartItemType[],
  failed: CartItemsErrorGroupType[],
};

type CartWishlistType = {
  id: string;
  name: string;
};

type CartWishlistSearchType = CartWishlistType[];

type FetchCartWishlistSearchResponseType = CartWishlistType[];

type FetchCartStatusResponseType = {
  size: number;
  value: number;
};

type CartStatusType = {
  count: number;
  value: number;
  lastInsertedQuantity: number | null;
  messages: string[],
  isLastInsertedNewItem?: boolean;
};

type AddCartToWishlistResponseType = {
  id: string;
};

type WishlistWithAddedCartType = {
  wishlistId: string;
};

export { CART_ITEM_ERROR_TYPE };
export type {
  AddCartToWishlistResponseType,
  AddItemsToCartResponseType,
  AddItemsToCartType,
  AddItemToCartResponseType,
  CartItemFailedType,
  CartItemsErrorGroupType,
  CartItemType,
  CartProductDetailsType,
  CartProductType,
  CartStatusType,
  CartStatusWithAddedItemType,
  CartSummaryType,
  CartType,
  CartWishlistSearchType,
  CartWishlistType,
  FetchCartItemResponseType,
  FetchCartResponseType,
  FetchCartStatusResponseType,
  FetchCartWishlistSearchResponseType,
  WishlistWithAddedCartType,
};
